<template>
  <div>
    <v-row>
      <v-col>
        <v-card class="pa-4 rounded-lg fill-height">
          <v-row align="center">
            <v-col cols="auto">
              <v-avatar
                size="100px"
                class="elevation-1"
              >
                <v-img
                  :src="player.photo_url"
                  width="100px"
                  height="100px"
                  class="rounded"
                >
                  <template v-slot:placeholder>
                    <v-icon x-large>
                      fal fa-user
                    </v-icon>
                  </template>
                </v-img>
              </v-avatar>
            </v-col>
            <v-col>
              <v-row
                class="d-flex flex-column"
                dense
              >
                <v-col class="title grey--text text--darken-4">
                  {{ player.first_name }} {{ player.last_name }}
                </v-col>
                <v-col>
                  {{ player.email }}
                </v-col>
                <v-col>
                  {{ $t('individual_analysis.data.age') }}: {{ playerAge }} years
                </v-col>
              </v-row>
            </v-col>
            <v-col
              v-if="isAdmin"
              cols="auto"
              align-self="start"
            >
              <v-btn
                icon
                :to="{ name: $routeNames.ADMIN_PLAYER_EDIT, params: { id: player.id } }"
              >
                <v-icon
                  small
                >
                  fa-light fa-gear
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              {{ $t('individual_analysis.data.team') }}: {{ player.team ? player.team.name : '-' }} {{ hasMultipleSports && player.team.sport ? '(' + $t(`sport.${player.team.sport.name.toLowerCase()}`) + ')' : null }}
              <span
                v-if="player.player_positions && player.player_positions.length"
                class="ml-3"
              >
                {{ $t("individual_analysis.data.position") }}:
                <v-chip
                  v-for="position in player.player_positions.slice(0, 3)"
                  :key="player.id + position.id"
                  x-small
                  label
                  class="mx-1"
                  color="primary"
                >
                  {{ position.short_name }}
                </v-chip>
              </span>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col>
              <div class="d-flex align-center">
                <div>
                  {{ $t("player_profile.status.label") }}:
                </div>
                <div>
                  <v-btn
                    text
                    color="primary"
                    class="text-none"
                    @click="statusDialog = true"
                  >
                    {{ player.player_status_id ? $t(`player_status.${player.player_status.name}`) : $t('button.select') }}
                  </v-btn>
                </div>
              </div>
            </v-col>
          </v-row>
          <v-row
            v-if="hasGpsModule || hasHeartRateModule"
            dense
          >
            <v-col>
              <div class="d-flex align-center">
                <div>
                  {{ $t("player_profile.performance_profile.label") }}:
                </div>
                <div>
                  <v-btn
                    text
                    color="primary"
                    class="text-none"
                    @click="$emit('edit:hr-profile')"
                  >
                    {{ $t('button.manage') }}
                  </v-btn>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
      <v-col v-if="$can('view-player-notes')">
        <v-card class="pa-2 rounded-lg fill-height">
          <v-card-title class="pl-2">
            <v-row
              no-gutters
              align="center"
            >
              <v-col class="title">
                {{ $t("player_profile.notes") }}
              </v-col>
              <v-col cols="auto">
                <v-btn
                  text
                  class="text-none"
                  color="primary"
                  :to="{ name: $routeNames.PLAYER_NOTES, params: { id: player.id } }"
                >
                  {{ $t("button.see_all") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>
          <v-card-text style="height: 70%">
            <div
              v-if="sortedNotes.length === 0"
              class="d-flex justify-center align-center fill-height"
            >
              <div>
                <img
                  :src="noNotes"
                  style="height: 100px; width: 100%; object-fit: contain;"
                >
                <div class="text-center text-caption mt-2">
                  {{ $t("notes_empty") }}
                </div>
              </div>
            </div>
            <div v-else>
              <div
                v-for="(note, index) in sortedNotes"
                :key="note.id"
              >
                <div
                  class="text-body-1 grey--text text--darken-3 mt-2 note-body"
                  v-html="processNoteText(note.text)"
                />
                <div class="d-flex justify-end text-caption mt-1">
                  {{ $dayjs(note.updated_at).format('dddd, MMMM D HH:mm:ss') }}
                </div>
                <v-divider
                  v-if="index < 1"
                  class="mt-2 mx-1"
                />
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col :cols="$can('view-player-media')">
        <v-card class="pa-0 rounded-lg fill-height">
          <v-card-title>
            <v-row
              no-gutters
              align="center"
            >
              <v-col class="title">
                {{ $t("player_profile.media") }}
              </v-col>
              <v-col cols="auto">
                <v-btn
                  text
                  class="text-none"
                  color="primary"
                  :to="{ name: $routeNames.PLAYER_MEDIA, params: { id: player.id } }"
                >
                  {{ $t("button.see_all") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>
          <v-card-text style="height: 70%">
            <div
              v-if="media.data.length === 0"
              class="d-flex justify-center align-center fill-height"
            >
              <div>
                <img
                  :src="noMedia"
                  style="height: 100px; width: 100%; object-fit: contain;"
                >
                <div class="text-center text-caption mt-2">
                  {{ $t("media.no_media") }}
                </div>
              </div>
            </div>
            <v-row
              v-else
              dense
              align="center"
            >
              <v-col
                v-for="(mediaItem, mediaItemIndex) in media.data"
                :key="mediaItem.id"
                cols="6"
              >
                <media-grid-item
                  :media-item="mediaItem"
                  :can-delete="$can('delete-event-media')"
                  :only-first-tag="true"
                  @preview="preview"
                  @delete="mediaItem.id ? startMediaDelete(mediaItem) : media.splice(mediaItemIndex, 1)"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <media-viewer-dialog
      :visible.sync="mediaViewerDialog.visible"
      :media="media.data"
      :view-media-id="mediaViewerDialog.media ? mediaViewerDialog.media.id : undefined"
      :view-only="!$can('edit-player-media')"
      :can-delete="$can('delete-player-media')"
      @media:updated="updateMedia"
    />

    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirmed="destroy()"
    />

    <card-dialog
      v-model="statusDialog"
      max-width="400px"
      :title="$t('card_dialog.select_player_status.title')"
    >
      <v-card-text
        class="pa-6"
      >
        <v-row
          class="flex-column"
          align="center"
        >
          <v-col
            v-for="status in playerStatuses.data"
            :key="status.id"
            class="py-2"
          >
            <v-btn
              block
              text
              class="text-none justify-start"
              :outlined="player.player_status_id === status.id"
              :style="{
                borderColor: player.player_status_id === status.id ? 'var(--v-primary-base)' : 'transparent',
                color: player.player_status_id === status.id ? 'var(--v-primary-base)' : ''
              }"
              @click="savePlayerStatus(status); statusDialog = false"
            >
              <v-icon
                v-if="status.icon"
                class="my-1 mr-2"
                small
              >
                {{ status.icon }}
              </v-icon>
              {{ $t(`player_status.${status.name}`) }}
              <v-spacer />
              <v-icon
                v-if="player.player_status_id === status.id"
              >
                fa-light fa-check
              </v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </card-dialog>
  </div>
</template>

<script>
import { PlayerApiService, PlayerStatusApiService } from '@/data/services'
import { getColorForValue, toCamelCase } from '@/utils/helpers'
import MediaGridItem from '@/components/helpers/MediaGridItem.vue'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog.vue'
import MediaViewerDialog from '@/components/dialogs/MediaViewerDialog.vue'
import { MediaApiService } from '@/data/services/MediaApiService'
import { RouteNames } from '@/router/routes'
import CardDialog from '@/components/helpers/CardDialog.vue'
import noMedia from '@/assets/img/player_info/no_media.svg'
import noNotes from '@/assets/img/player_info/empty_notes.svg'

export default {
  name: 'PlayerDetailsCard',
  // eslint-disable-next-line vue/no-unused-components
  components: {
    CardDialog,
    MediaViewerDialog,
    DeleteConfirmationDialog,
    MediaGridItem
  },
  props: {
    player: {
      type: Object,
      required: true
    },
    stats: {
      type: Object,
      default: undefined
    }
  },
  data() {
    return {
      playerStatuses: {
        data: [],
        loading: false,
        loaded: false
      },
      media: {
        loading: false,
        data: [],
        pagination: {
          current_page: 1,
          last_page: undefined,
          per_page: 2
        }
      },
      filters: {
        eventType: undefined,
        search: '',
        dateRange: [this.$dayjs().subtract(12, 'month').format('YYYY-MM-DD'), this.$dayjs().format('YYYY-MM-DD')],
        tags: []
      },
      mediaViewerDialog: {
        visible: false,
        media: undefined
      },
      statusDialog: false,
      deleteConfirmationDialogVisible: false,
      deleting: false,
      playerStatusId: null,
      savingPlayerStatus: false,
      noMedia,
      noNotes
    }
  },
  computed: {
    RouteNames() {
      return RouteNames
    },
    playerAge() {
      if (!this.player.birth_date) return ''

      return this.$dayjs().diff(this.$dayjs(this.player.birth_date), 'year')
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    showWellnessData() {
      return !this.user.organisation_settings.has_short_wellness_feedback
    },
    hasItlModule() {
      return this.user.organisation_settings.has_itl_module || false
    },
    isAdmin() {
      return this.$can('manage-organisation')
    },
    hasGpsModule() {
      return this.user.organisation_settings.has_gps_module || false
    },
    hasHeartRateModule() {
      return this.user.organisation_settings.has_heart_rate_module || false
    },
    hasMultipleSports() {
      return this.user.organisation_settings.has_multiple_sports
    },
    sortedNotes() {
      if (!this.player.notes) {
        return []
      }
      const pinnedNotes = this.player.notes.filter(note => note.is_pinned)

      if (pinnedNotes.length > 0) {
        return pinnedNotes
          .sort((a, b) => {
            if (a.updated_at > b.updated_at) return -1
            if (a.updated_at < b.updated_at) return 1
            return 0
          })
          .slice(0, 2)
      }

      return [...this.player.notes]
        .sort((a, b) => {
          if (a.updated_at > b.updated_at) return -1
          if (a.updated_at < b.updated_at) return 1
          return 0
        })
        .slice(0, 2)
    }
  },
  watch: {
    'player.player_status_id': (newValue) => {

    }
  },
  created() {
    this.fetchPlayerStatuses()
    this.fetchMedia()
  },
  methods: {
    toCamelCase,
    getColorForValue,
    async fetchPlayerStatuses() {
      if (this.playerStatuses.loaded) return

      try {
        this.playerStatuses.loading = true

        const response = await PlayerStatusApiService.getAll()

        this.playerStatuses.data = response.data
        this.playerStatuses.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.playerStatuses.loading = false
      }
    },
    async fetchMedia() {
      try {
        this.media.loading = true

        const queryParams = {
          page: this.media.pagination.current_page,
          per_page: this.media.pagination.per_page,
          event_type: this.filters.eventType,
          from: this.filters.dateRange[0] ? this.filters.dateRange[0] : undefined,
          to: this.filters.dateRange[1] ? this.filters.dateRange[1] : undefined,
          search: this.filters.search || undefined,
          tags: this.filters.tags
        }

        const resp = await PlayerApiService.getMedia(this.player.id, queryParams)

        this.media.data = resp.data
        this.media.pagination = resp.meta
      } catch (e) {
        this.media.data = []
        this.media.pagination.current_page = 1
        this.media.pagination.last_page = 1
      } finally {
        this.media.loading = false
      }
    },
    async savePlayerStatus(playerStatus) {
      try {
        this.savingPlayerStatus = true

        await PlayerApiService.updateStatus(this.player.id, {
          player_status_id: playerStatus.id
        })

        this.player.player_status_id = playerStatus.id
        this.player.player_status = playerStatus

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.player_status_update.success'),
          color: 'success'
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.player_status_update.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.savingPlayerStatus = false
      }
    },
    preview(media) {
      this.mediaViewerDialog.visible = true
      this.mediaViewerDialog.media = media
    },
    startMediaDelete(media) {
      this.deleteMedia.media = media
      this.deleteMedia.dialog = true
    },
    updateMedia(media) {
      const index = this.media.data.findIndex(m => m.id === media.id)

      if (index === -1) return

      this.media.data.splice(index, 1, media)
    },
    async destroy() {
      if (!this.deleteMedia) return

      const id = this.deleteMedia.id

      try {
        this.deleting = true

        await MediaApiService.delete(id)

        const index = this.media.findIndex(m => m.id === id)

        if (index > -1) this.media.splice(index, 1)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.media_delete.success'),
          color: 'success'
        })

        this.deleteConfirmationDialogVisible = false

        this.deleteMedia = undefined

        this.$emit('update')
      } catch (e) {

      } finally {
        this.deleting = false
      }
    },
    processNoteText(text, maxLength = 125) {
      text = text.replace(/(\S{50,})/g, match =>
        match.replace(/(.{30})/g, '$1<wbr>')
      )

      const div = document.createElement('div')
      div.innerHTML = text
      if ((div.textContent || '').length <= maxLength) return text

      let textCount = 0; let result = ''; let inTag = false

      for (const char of text) {
        if (char === '<') inTag = true
        result += char
        if (char === '>') inTag = false
        if (!inTag && char !== '<' && ++textCount >= maxLength) break
      }

      return result + '...'
    }

  }
}
</script>
